import { Card, Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine
} from "recharts";

/* ===================== Helpers ===================== */

const Dot = ({ cx, cy, r, fill }) => {
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={r} fill={fill} />;
};

const ValueLabel = ({ x, y, value, offset }) => {
  if (x == null || y == null) return null;

  // Use different baseline for top (negative offset) vs bottom (positive offset)
  const baseline = offset < 0 ? "auto" : "hanging";

  return (
    <text
      x={x}
      y={y + offset}
      textAnchor="middle"
      dominantBaseline={baseline}
      fill="#11111180"
      fontSize={8}
      fontWeight={500}
    >
      ${value.toFixed(2)}
    </text>
  );
};

/* ---------- Custom X Axis Tick ---------- */
const CustomXAxisTick = ({ x, y, payload, index, xLabels }) => {
  let anchor = "middle";
  if (index === 0) anchor = "start";
  if (index === xLabels.length - 1) anchor = "end";

  return (
    <text
      x={x}
      y={y + 16}
      textAnchor={anchor}
      fill="#111111"
      fontSize={10}
      fontWeight={200}
    >
      {xLabels[payload.value]}
    </text>
  );
};

/* ===================== Component ===================== */

export default function BrandBaseVsPromoChart({
  title,
  xLabels,
  series,
  dataMatrix,
  yTicks
}) {
  const OFFSET = 0.18;

  const promoLast = [];
  const baseLast = [];
  const promoCurrent = [];
  const baseCurrent = [];
  const connectors = [];

  /* ===== Build Dumbbell Data ===== */
  xLabels.forEach((_, i) => {
    const [pLast, bLast, pCur, bCur] = dataMatrix.actual[i];

    promoLast.push({ x: i - OFFSET, y: pLast });
    baseLast.push({ x: i - OFFSET, y: bLast });

    promoCurrent.push({ x: i + OFFSET, y: pCur });
    baseCurrent.push({ x: i + OFFSET, y: bCur });

    connectors.push({ x: i - OFFSET, y1: pLast, y2: bLast });
    connectors.push({ x: i + OFFSET, y1: pCur, y2: bCur });
  });
  const chartWidth = Math.max(xLabels.length * 140, 900);
  return (
    <Card
      sx={{
        p: 3,
        pt: 6,
        borderRadius: "12px !important",
        position: "relative",
        boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          position: "absolute",
          top: "-10px",
          left: 12,
          right: 12,
          height: "36px!important",
          background: "#115f9c",
          borderRadius: "4px !important",
          display: "flex",
          alignItems: "center",
          px: "12px",
          color: "#fff",
          fontWeight: 200,
          fontSize: { xs: 12, sm: 12, md: 16 },
          boxShadow:"0px 1px 16px rgba(128, 203, 255, 0.5)"
        }}
      >
        {title}
      </Box>

      {/* CHART */}
      <Box sx={{
        width: "100%",
        height: 440,
        overflowX: "auto",
        overflowY: "hidden",
      }}>
        <Box
          sx={{
            width: {
              xs: chartWidth,   //  enable horizontal scroll on mobile
              sm: chartWidth,   // enable horizontal scroll on tablet
              md: chartWidth,
              lg: "100%"
            },
            minWidth: {
              xs: chartWidth,
              sm: chartWidth,
              md: chartWidth,
              lg: "100%"
            },
            height: "100%",
          }}
        >
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 20, bottom: 20, left: 30, right: 40 }}>
              <CartesianGrid stroke="#1111110D" vertical={false} />

              {/* Vertical padding for top & bottom labels */}
              <YAxis
                yAxisId={0}
                domain={["dataMin - 0.3", "dataMax + 0.35"]}
                ticks={yTicks}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                label={{
                  value: "Price ($)",
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                }}
              />

              <XAxis
                xAxisId={0}
                type="number"
                dataKey="x"
                domain={[-0.6, xLabels.length - 0.4]}
                ticks={xLabels.map((_, i) => i)}
                axisLine={{ stroke: "#DADADA" }}
                tickLine={false}
                interval={0}
                tick={(props) => (
                  <CustomXAxisTick {...props} xLabels={xLabels} />
                )}
              />

              <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />

              {/* CONNECTORS */}
              {connectors.map((c, i) => (
                <ReferenceLine
                  key={i}
                  segment={[
                    { x: c.x, y: c.y1 },
                    { x: c.x, y: c.y2 }
                  ]}
                  stroke="#BDBDBD"
                  strokeWidth={2}
                />
              ))}

              {/* LAST PERIOD - Promo (gray, bottom) */}
              <Scatter
                data={promoLast}
                xAxisId={0}
                yAxisId={0}
                dataKey="y"
                isAnimationActive={false}
                shape={(p) => (
                  <g>
                    <Dot cx={p.cx} cy={p.cy} r={8} fill="#8C8C8C" />
                    <ValueLabel x={p.cx} y={p.cy} value={p.payload.y} offset={18} />
                  </g>
                )}
              />

              {/* LAST PERIOD - Base (olive yellow, top) */}
              <Scatter
                data={baseLast}
                xAxisId={0}
                yAxisId={0}
                dataKey="y"
                isAnimationActive={false}
                shape={(p) => (
                  <g>
                    <Dot cx={p.cx} cy={p.cy} r={9} fill="#D8C72C" />
                    <ValueLabel x={p.cx} y={p.cy} value={p.payload.y} offset={-22} />
                  </g>
                )}
              />

              {/* THIS PERIOD - Promo (black, bottom) */}
              <Scatter
                data={promoCurrent}
                xAxisId={0}
                yAxisId={0}
                dataKey="y"
                isAnimationActive={false}
                shape={(p) => (
                  <g>
                    <Dot cx={p.cx} cy={p.cy} r={8} fill="#000000" />
                    <ValueLabel x={p.cx} y={p.cy} value={p.payload.y} offset={18} />
                  </g>
                )}
              />

              {/* THIS PERIOD - Base (bright yellow, top) */}
              <Scatter
                data={baseCurrent}
                xAxisId={0}
                yAxisId={0}
                dataKey="y"
                isAnimationActive={false}
                shape={(p) => (
                  <g>
                    <Dot cx={p.cx} cy={p.cy} r={9} fill="#FFE000" />
                    <ValueLabel x={p.cx} y={p.cy} value={p.payload.y} offset={-22} />
                  </g>
                )}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Box>

      </Box>

      {/* LEGEND */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
          mt: '16px'
        }}
      >
        {series.map((s) => (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: s.color
              }}
            />
            <Typography fontSize={10} style={{ color: "#111111" ,fontWeight:200}}>{s.label}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}
